import { observer } from 'mobx-react-lite'
import {
  ResponsiveContainer,
  Tooltip,
  XAxis,
  Area,
  Line,
  ComposedChart,
} from 'recharts'
import { format } from 'date-fns'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { Box, Typography, useTheme } from '@mui/material'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { AssignedChart, WishlistedChart } from '../../model/my-team-elearning'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

export type TeamLearningChartProps = {
  assignedData: AssignedChart[]
  wishlistedData: WishlistedChart[]
  isLoading: boolean
  maxWidth?: number | string
  assignedTitle: string
  wishlistedTitle: string
}

function ChartCard({
  title,
  data,
  dataKey,
  isLoading,
}: {
  title: string
  data: unknown[]
  dataKey: string
  isLoading: boolean
}) {
  const theme = useTheme()
  const { translation } = useLocalizationStore()
  
  return (
    <AppPaper sx={{ padding: '10px 15px', borderRadius: '10px', gap: 10, width: '100%' }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
        {title}
      </Typography>
      <AppBox sx={styles.box}>
        {!isLoading && data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data}>
              <XAxis
                dataKey="date"
                padding={{ left: 15, right: 15 }}
                tickFormatter={date => format(new Date(date), 'd MMM')}
                fontSize={10}
              />
              <Tooltip
                formatter={(value, name) => [`${value}`, `${name}`]}
                contentStyle={{
                  backgroundColor: '#161616',
                  borderRadius: '10px',
                  padding: '0.5em',
                }}
                itemStyle={{
                  fontSize: 11,
                  padding: '0.1em 0',
                  margin: '0',
                }}
                labelStyle={{
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 'bold',
                  paddingBottom: '0.1em',
                }}
              />
              <Area
                type="monotone"
                dataKey={dataKey}
                name={title}
                stroke="#27827A"
                fill="#27827A40"
              />
              <Line
                type="monotone"
                dataKey="completed"
                name="Completed"
                stroke={theme.palette.secondary.light}
                fill={`${theme.palette.secondary.light}40`}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {isLoading && (
          <AppSkeleton
            count={1}
            direction="column"
            rects={[{ width: '100%', height: 75, color: 'background.dark' }]}
          />
        )}
        {data.length === 0 && (
          <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>{translation.noData()}</Caption>
        )}
      </AppBox>
    </AppPaper>
  )
}

export const TeamLearningChart = observer(({ assignedData, wishlistedData, isLoading, maxWidth = '100%', assignedTitle, wishlistedTitle }: TeamLearningChartProps) => {
  return (
    <Box sx={{ ...styles.root, width: { xs: '100%', md: maxWidth }, maxWidth: { xs: '100%', md: maxWidth } }}>
      <ChartCard
        title={assignedTitle}
        data={assignedData}
        dataKey="assigned"
        isLoading={isLoading}
      />
      <ChartCard
        title={wishlistedTitle}
        data={wishlistedData}
        dataKey="wishlisted"
        isLoading={isLoading}
      />
    </Box>
  )
})

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: 15,
  },
  box: {
    width: '100%',
    height: 100,
  },
}